import { useMemo } from 'react'
import CartEntity from '@/entities/api/cart/cart'
import CouponsEntity, { TypesCoupon } from '@/entities/api/coupons/coupon'

interface CartCalculationsResult {
    totalItems: number
    totalPrice: number
    totalShipping: number
    couponDiscount: number
    subtotalAfterCoupon: number
    grandTotal: number
}

interface UseCartCalculationsProps {
    items: CartEntity[]
    coupon?: Partial<CouponsEntity> | null
}

const useCartCalculations = ({ items, coupon }: UseCartCalculationsProps): CartCalculationsResult => {
    return useMemo(() => {
        const isCouponValid = (coupon: Partial<CouponsEntity>): boolean => {
            if (!coupon.expiration) return false
            const expirationDate = new Date(coupon.expiration)
            const currentDate = new Date()
            return expirationDate > currentDate
        }

        const calculateItemTotal = (item: CartEntity): number => {
            const attribute = item.product.attributes?.[0]
            if (!attribute) return 0

            const price = attribute.price || 0
            const quantity = attribute.quantity || 0
            const discount = item.product.discount || 0

            const discountedPrice = price - (price * discount) / 100
            return discountedPrice * quantity
        }


        const calculateItemShipping = (item: CartEntity): number => {
            if (!item.product.shipping_info) return 0

            const shippingInfo = item.product.shipping_info
            const attribute = item.product.attributes?.[0]
            const quantity = attribute?.quantity || 0

            if (
                !shippingInfo.isHaveShipping ||
                shippingInfo.type === 'free' ||
                quantity === 0
            ) {
                return 0
            }

            if (shippingInfo.type === 'pay') {
                const baseShippingPrice = shippingInfo.price || 0
                const totalAttributes = item.product.attributes?.reduce((acc, attr) => {
                    return acc + (attr.quantity || 0)
                }, 0) || 0

                let totalShipping = baseShippingPrice

                if (totalAttributes > 1) {
                    const additionalAttributes = totalAttributes - 1
                    totalShipping += baseShippingPrice * 0.5 * additionalAttributes
                }

                return totalShipping
            }

            return 0
        }

        const calculateCouponDiscount = (
            subtotal: number,
            coupon: Partial<CouponsEntity> | null | undefined
        ): number => {
            if (!coupon || !isCouponValid(coupon)) return 0

            switch (coupon.type) {
                case TypesCoupon.DISCOUNT:
                    const discountPercentage = coupon.discount || 0
                    return (subtotal * discountPercentage) / 100
                case TypesCoupon.AMOUNT:
                    const fixedAmount = coupon.amount || 0
                    return Math.min(fixedAmount, subtotal)
                default:
                    return 0
            }
        }

        const validItems = items.filter(item => {
            const attribute = item.product.attributes?.[0]
            return attribute && attribute.quantity && attribute.quantity > 0
        })

        const totalItems = validItems.reduce((total, item) => {
            return total + (item.product.attributes?.[0]?.quantity || 0)
        }, 0)

        const totalPrice = validItems.reduce((total, item) => {
            return total + calculateItemTotal(item)
        }, 0)

        const totalShipping = validItems.reduce((total, item) => {
            return total + calculateItemShipping(item)
        }, 0)

        const couponDiscount = calculateCouponDiscount(totalPrice, coupon)

        const subtotalAfterCoupon = Math.max(0, totalPrice - couponDiscount)

        const grandTotal = subtotalAfterCoupon + totalShipping

        return {
            totalItems,
            totalPrice,
            totalShipping,
            couponDiscount,
            subtotalAfterCoupon,
            grandTotal,
        }
    }, [items, coupon])
}

export default useCartCalculations